/**
 * De onde o documento saiu e o vestígio de assinatura do arquivo (E97.F4.T04, ADR-0082).
 *
 * Módulo próprio porque `QuadroVerificacaoAssinatura` e o painel do arquivo já estão
 * perto do teto. Os dois blocos só leem o que o backend apurou: nenhum veredito novo.
 * - `OrigemDoDocumento`: o documento contido num arquivo único — a assinatura e os
 *   metadados examinados são os desse arquivo (ADR-0082 §2.3).
 * - `VestigioDeAssinatura`: o `/Sig` fora do formulário, com a faixa de outro arquivo.
 */
import { MonoData } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { VestigioDeAssinaturaData } from '@/lib/exame_eletronico_assinatura_digital';
import { faixaDePaginas, type OrigemDoArquivoData } from '@/lib/exame_eletronico_arquivo_unico';
import { formatarDeclarado } from '@/lib/exame_eletronico_datas_declaradas';

import { Titulo } from './QuadroVerificacaoTabelas';

const ta = ptBR.exameEletronico.quadro.arquivo;
const tv = ptBR.exameEletronico.quadro.vestigio;
const tu = ptBR.exameEletronico.arquivoUnico;

export function OrigemDoDocumento({
  origem,
}: {
  origem: OrigemDoArquivoData | null;
}): JSX.Element | null {
  if (origem === null) return null;
  const paginas = (origem.paginas.length === 1 ? tu.pagina : tu.paginas).replace(
    '{paginas}',
    faixaDePaginas(origem.paginas),
  );
  return (
    <section data-testid="quadro-origem-do-documento">
      <Titulo>{ta.origemTitulo}</Titulo>
      <p className="max-w-prose text-body-sm text-fg">
        {ta.origemCorpo.replace('{nome}', origem.nome_do_arquivo).replace('{paginas}', paginas)}
      </p>
      {origem.sha256 ? (
        <p className="mt-sc-1 text-caption text-fg-dim">
          {ta.origemHash.split('{hash}')[0]}
          <MonoData>{origem.sha256}</MonoData>
        </p>
      ) : null}
    </section>
  );
}

function Linha({ rotulo, valor }: { rotulo: string; valor: string }): JSX.Element {
  return (
    <li>
      <span className="text-fg-dim">{rotulo}:</span> <span className="text-fg">{valor}</span>
    </li>
  );
}

export function VestigioDeAssinatura({
  vestigio,
}: {
  vestigio: VestigioDeAssinaturaData | null | undefined;
}): JSX.Element | null {
  if (!vestigio) return null;
  const validade = `${formatarDeclarado(vestigio.valido_de)} – ${formatarDeclarado(vestigio.valido_ate)}`;
  return (
    <section
      className="border border-warning bg-warning-50 px-sc-3 py-sc-2"
      data-testid="quadro-vestigio-de-assinatura"
    >
      <Titulo>{tv.titulo}</Titulo>
      <p className="max-w-prose text-body-sm text-warning-700">{tv.corpo}</p>
      <ul className="mt-sc-2 space-y-0.5 text-body-sm">
        <Linha rotulo={tv.titular} valor={vestigio.titular ?? tv.naoInformado} />
        <Linha rotulo={tv.emissor} valor={vestigio.emissor ?? tv.naoInformado} />
        <Linha rotulo={tv.validade} valor={validade} />
        <Linha rotulo={tv.declaradaEm} valor={formatarDeclarado(vestigio.declarada_em)} />
      </ul>
      <ul className="mt-sc-2 list-disc space-y-0.5 pl-sc-4 text-body-sm text-warning-700">
        {vestigio.vencido_na_data_declarada ? <li>{tv.vencido}</li> : null}
        {!vestigio.faixa_corresponde_ao_arquivo && vestigio.bytes_assinados != null ? (
          <li>
            {tv.faixa
              .replace('{assinados}', String(vestigio.bytes_assinados))
              .replace('{tamanho}', String(vestigio.tamanho_do_arquivo))}
          </li>
        ) : null}
        {vestigio.carimbo_do_tempo ? null : <li>{tv.semCarimbo}</li>}
      </ul>
    </section>
  );
}
